// =============================================================================
// STREETSTICKER.COM — EGGSHELL STICKER PRICING ENGINE (US MARKET)
// =============================================================================
// Hybrid model — calibrated for the US market with all costs (printing,
// production, US shipping, no sales tax / duties) baked into the price:
//
//   linearPerUnit     = (SETUP_PER_STICKER + BASE_PER_IN2 * area_in2) * shapeMult
//   stueckpreis_basis = max(MIN_UNIT_PRICE, linearPerUnit)
//   stueckpreis_final = stueckpreis_basis * (1 - quantityDiscount)
//   totalPrice        = max(MIN_ORDER_TOTAL, stueckpreis_final * qty)
//   totalPrice        = Math.round(totalPrice)   // ROUND TO WHOLE DOLLAR
//
// SETUP_PER_STICKER reflects fixed per-sticker overhead (print head setup,
// die-cutting, handling). Makes small stickers more expensive per in² than
// large ones — matches what serious print competitors charge.
//
// MIN_ORDER_TOTAL covers US shipping (~$40) + minimum margin on small orders.
// =============================================================================

const PRICING = {
  BASE_PER_IN2:      0.045,  // USD per in² (material + ink)
  SETUP_PER_STICKER: 0.15,   // USD per-sticker setup base (print, die-cut, handling)
  MIN_UNIT_PRICE:    0.10,   // Hard floor per sticker
  MIN_ORDER_TOTAL:   90,     // Minimum total order value in USD (covers US shipping)
  TIER_STEP_UP:      15,     // Each tier must be at least $X above the previous
                             // (only kicks in when the previous tier was already
                             // sitting on a floor — keeps the staffel monotone).

  // Shape multiplier — angular shapes are more expensive to cut/produce.
  SHAPE_MULTIPLIER: {
    square:    1.13,
    rectangle: 1.15,
    round:     1.00
  },

  // Volume tiers — bigger order, better per-sticker price.
  // 100 was dropped (too small for US shipping). 10000 added for power users.
  QUANTITY_TIERS: [
    { qty: 250,   discount: 0.00 },
    { qty: 500,   discount: 0.18 },
    { qty: 1000,  discount: 0.30 },
    { qty: 2000,  discount: 0.42 },
    { qty: 5000,  discount: 0.54 },
    { qty: 10000, discount: 0.62 }
  ],

  SHAPES: {
    square: {
      label: 'Square',
      icon: 'square',
      sizes: [
        { id: 'sq-2', w: 2, h: 2, label: '2 × 2 in' },
        { id: 'sq-3', w: 3, h: 3, label: '3 × 3 in' },
        { id: 'sq-4', w: 4, h: 4, label: '4 × 4 in' },
        { id: 'sq-5', w: 5, h: 5, label: '5 × 5 in' }
      ]
    },
    rectangle: {
      label: 'Rectangle',
      icon: 'rectangle',
      sizes: [
        { id: 'rc-2x3', w: 2, h: 3, label: '2 × 3 in' },
        { id: 'rc-3x4', w: 3, h: 4, label: '3 × 4 in' },
        { id: 'rc-2x5', w: 2, h: 5, label: '2 × 5 in' },
        { id: 'rc-2x8', w: 2, h: 8, label: '2 × 8 in' }
      ]
    },
    round: {
      label: 'Round',
      icon: 'circle',
      sizes: [
        { id: 'rd-2', w: 2, h: 2, label: 'Ø 2 in' },
        { id: 'rd-3', w: 3, h: 3, label: 'Ø 3 in' },
        { id: 'rd-4', w: 4, h: 4, label: 'Ø 4 in' },
        { id: 'rd-5', w: 5, h: 5, label: 'Ø 5 in' }
      ]
    }
  },

  CUSTOM_MIN_IN: 1,
  CUSTOM_MAX_IN: 36
};

// -----------------------------------------------------------------------------
// AREA (in²)
// -----------------------------------------------------------------------------
function calcArea(shape, w, h) {
  if (shape === 'round') {
    const r = w / 2;
    return Math.PI * r * r;
  }
  return w * h;
}

// -----------------------------------------------------------------------------
// CORE PRICE
// -----------------------------------------------------------------------------
function priceFor(areaIn2, qty, shape) {
  const tier = PRICING.QUANTITY_TIERS.find(t => t.qty === qty) || closestTier(qty);
  const shapeMult = (shape && PRICING.SHAPE_MULTIPLIER[shape]) || 1.0;

  const linearPerUnit = (PRICING.SETUP_PER_STICKER + PRICING.BASE_PER_IN2 * areaIn2) * shapeMult;
  const basePerUnit = Math.max(PRICING.MIN_UNIT_PRICE, linearPerUnit);
  const discountedPerUnit = basePerUnit * (1 - tier.discount);

  const rawTotal = discountedPerUnit * qty;

  // Hard floor 1: minimum order value
  const minimumApplied = rawTotal < PRICING.MIN_ORDER_TOTAL;
  let totalUnrounded = minimumApplied ? PRICING.MIN_ORDER_TOTAL : rawTotal;
  let tieredStepApplied = false;

  // Hard floor 2: each tier must be at least TIER_STEP_UP above the previous,
  // but only if the previous tier was already on a floor.
  const tierIdx = PRICING.QUANTITY_TIERS.findIndex(t => t.qty === qty);
  if (tierIdx > 0 && PRICING.TIER_STEP_UP > 0) {
    const prevTier = PRICING.QUANTITY_TIERS[tierIdx - 1];
    const prev = priceFor(areaIn2, prevTier.qty, shape);
    if (prev.minimumApplied || prev.tieredStepApplied) {
      const stepFloor = prev.total + PRICING.TIER_STEP_UP;
      if (totalUnrounded < stepFloor) {
        totalUnrounded = stepFloor;
        tieredStepApplied = true;
      }
    }
  }

  // ROUND TOTAL TO WHOLE DOLLAR (US market preference)
  const total = Math.round(totalUnrounded);
  const effectivePerUnit = total / qty;

  return {
    qty,
    areaIn2,
    basePerUnit,
    discountedPerUnit: effectivePerUnit,
    discount: tier.discount,
    total,
    minimumApplied,
    tieredStepApplied,
    floorActive: minimumApplied || tieredStepApplied
  };
}

function closestTier(qty) {
  return PRICING.QUANTITY_TIERS.reduce((a, b) =>
    Math.abs(b.qty - qty) < Math.abs(a.qty - qty) ? b : a
  );
}

// -----------------------------------------------------------------------------
// SAVINGS vs. smallest tier
// -----------------------------------------------------------------------------
function calcSavings(areaIn2, qty, shape) {
  const ref = priceFor(areaIn2, PRICING.QUANTITY_TIERS[0].qty, shape);
  const cur = priceFor(areaIn2, qty, shape);

  const referencePerUnit = ref.discountedPerUnit;
  const currentPerUnit   = cur.discountedPerUnit;

  const wouldHaveCost = referencePerUnit * qty;
  const savedAbsolute = Math.max(0, wouldHaveCost - cur.total);
  const savedPercent  = referencePerUnit > 0 && currentPerUnit < referencePerUnit
    ? ((referencePerUnit - currentPerUnit) / referencePerUnit) * 100
    : 0;

  return {
    referencePerUnit,
    currentPerUnit,
    savedAbsolute,
    savedPercent
  };
}

// -----------------------------------------------------------------------------
// FORMATTERS
// -----------------------------------------------------------------------------
// Whole-dollar formatter for totals: "$261"
function fmtUSD(n) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 0, maximumFractionDigits: 0
  }).format(Math.round(n));
}

// Cents formatter for per-unit prices: "$0.26"
function fmtUSDcents(n) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 2, maximumFractionDigits: 2
  }).format(n);
}

if (typeof module !== 'undefined') {
  module.exports = { PRICING, calcArea, priceFor, calcSavings, fmtUSD, fmtUSDcents };
}
